import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {IGetSimilar} from '../../../services/rest-data.service';
import {ServerResults} from '../../../services/common-datatypes';
import {AutoUnsubscribe, RequiredInput} from '../../../decorators/common.decorators';
import {Subscription} from 'rxjs';
import {IonInput} from '@ionic/angular';

const MAX_POSSIBILITIES = 20;

@Component({
    selector: 'app-database-lookup-input',
    templateUrl: './database-lookup-input.component.html',
    styleUrls: ['./database-lookup-input.component.scss'],
})
export class DatabaseLookupInputComponent implements OnInit, OnDestroy {

    @Input() inline = false;
    @Input() @RequiredInput translationPrefix!: string;
    @Input() allowExisting = true;
    @Input() showExisting = true;
    @Input() onlyExisting = false;
    @Input() showInitialChoices = true;
    @Input() showSame = false;
    @Input() showChosen = false;
    @Input() maxPossibilities = MAX_POSSIBILITIES;
    private _allowed: string[] = [];
    @Input() allowed: string[] = [];
    @Input() notAllowed: string[] = [];
    @Input() boundLookupFunction: IGetSimilar | undefined = undefined;
    @Input() parentLookup: string = '';
    @Input() validatorStringPattern: string = '';
    @Input() invalidPatternRegExp: RegExp | null = null;
    @Input() exactLength: number = 0;
    @Input() required = false;
    @Input() minLength = '1';
    @Input() maxLength = '255';
    @Input() maxLookup = MAX_POSSIBILITIES;
    @Input() size = 10;
    @Input() inputMode: string = '';
    @Input() type: string = '';
    @Input() startString: string = '';
    private _value: string = '';
    @Input()
    set value(value: string) {
        // if (!value) return;
        this._value = value;
        this.clearInput(value);
    }

    private initialValue: string | null = null;
    private lastLookupValue: string = '';

    @Output() valueChange = new EventEmitter<string>();
    @Output() valueChosen = new EventEmitter<string>();
    @ViewChild('input') input!: IonInput | undefined;

    public valid = false;
    public errorDuplicate = false;
    public errorShort = false;
    public errorNotExisting = false;
    public errorNotAllowed = false;
    public hasValue = false;
    public inputGroup: FormGroup;
    private inputControl: AbstractControl;
    public numPossibilities: number = 0;
    public possibilities: string[] = [];
    private chosenValue: string = '';

    @AutoUnsubscribe('ngOnDestroy')
    private subscriptions: Subscription[] = [];

    constructor(private fb: FormBuilder) {
        let inputControlParams: any[] = [];
        if (this.validatorStringPattern && (0 < this.validatorStringPattern.length)) {
            inputControlParams = ['', Validators.pattern(this.validatorStringPattern)];
        }

        this.inputGroup = this.fb.group({
            inputControl: inputControlParams
        });

        this.inputControl = this.inputGroup.controls['inputControl'];
    }

    ngOnInit() {
        if (this.input) this.input.setFocus();
        if (this.showInitialChoices) this.initialValue = null;
        else this.initialValue = (' ' + this._value).slice(1); // copy of string

        // private copy of allowed values
        this.allowed.forEach((value) => {
            this._allowed.push((' ' + value).slice(1)); // copy of string
        });

        this.subscriptions.push(
            this.inputControl.valueChanges.subscribe((inputValue: string) => {
                this.testValue(inputValue);
            })
        );

        this.testValue(this._value);
    }

    ngOnDestroy(): void {
    }

    lookup() {
        let value = this._value;
        if (!value) value = '';
        if (this.lastLookupValue !== value) {
            this.lastLookupValue = (' ' + value).slice(1); // store copy
            if (this.boundLookupFunction && this.showExisting) {
                this.boundLookupFunction(value, this.parentLookup, this.maxLookup).then((data: ServerResults<string>) => {
                    this.validateLookup(value, data);
                });
            }
        }
    }

    validateInput(value: string): string {
        if (this.invalidPatternRegExp) value = value.replace(this.invalidPatternRegExp, '');
        if (this.startString &&
            ((!this.exactLength && (value.length > 0))
                || (this.exactLength && (value.length >= this.exactLength - this.startString.length)))
            && !value.startsWith(this.startString)) value = this.startString + value.substring(0, this.exactLength);
        return value;
    }

    validateLookup(value: string, data: ServerResults<string>): void {
        if (this.showExisting) {
            this.numPossibilities = data.total;
            this.possibilities = [];
            if (this.showInitialChoices || (this.initialValue !== value)) {
                this.initialValue = null;
                if (data.total < this.maxPossibilities) {
                    data.items.forEach((item) => {
                        if ((this.showSame || (item !== this._value)) &&
                            (this.showChosen || (item !== this.chosenValue))) this.possibilities.push(item);
                    });
                }
            }
        }
        if (this.exactLength && (value.length === this.exactLength) && !this.allowExisting) {
            let valid = false;
            if (data.total > 0) {
                for (let i = 0; i < this._allowed.length; i++) {
                    if (this._allowed[i] === value) {
                        valid = true;
                        break;
                    }
                }
            } else valid = true;
            if (valid) this.setValid(value);
            else {
                this.errorDuplicate = true;
                this.setInvalid(value);
            }
        } else this.errorDuplicate = false;
        if (this.onlyExisting && (data.total <= 0)) {
            this.errorNotExisting = true;
            this.setInvalid(value);
        }
    }

    testValue(value: string) {
        if (!value) return this.setInvalid(value);
        if (value !== this.chosenValue) this.chosenValue = '';

        const corectedValue = this.validateInput(value);

        if (value !== corectedValue) {
            this.setInvalid(corectedValue);
            this.inputControl.setValue(corectedValue);
            return;
        }

        let valid = true;
        if (this.exactLength && (corectedValue.length < this.exactLength)) {
            valid = false;
            this.errorShort = true;
            this.errorDuplicate = false;
            this.errorNotAllowed = false;
        } else if (this.notAllowed.length > 0) {
            for (let i = 0; i < this.notAllowed.length; i++) {
                if (this.notAllowed[i] === corectedValue) {
                    valid = false;
                    this.errorShort = false;
                    this.errorDuplicate = false;
                    this.errorNotAllowed = true;
                    break;
                }
            }
        }

        if (valid) this.setValid(corectedValue);
        else this.setInvalid(corectedValue);

        if (this.boundLookupFunction && (this.showExisting || (this.exactLength === corectedValue.length))) {

            this.boundLookupFunction(value, this.parentLookup, this.maxLookup).then((data: ServerResults<string>) => {
                this.validateLookup(corectedValue, data);
            });
        }
    }


    setValid(value: string) {
        this.valid = true;
        this.errorShort = false;
        this.errorDuplicate = false;
        this.errorNotExisting = false;
        this.hasValue = true;
        this._value = value;
        this.valueChange.next(value);
    }

    setInvalid(value: string) {
        if (this.valid) this.valueChange.next(''); // only invalidate a previous valid value
        this.valid = false;
        this.hasValue = !(!value || value.length === 0);
        this._value = value;
    }


    clearInput(value: string = '') {
        // this.inputControl.reset();
        this.numPossibilities = 0;
        this.possibilities = [];
        this.hasValue = false;
        this.errorShort = false;
        this.errorDuplicate = false;
        this.errorNotExisting = false;
        this.valid = false;
        this.chosenValue = '';
        this._value = value;
        this.inputControl.setValue(value);
    }

    choose(value: string) {
        this._value = value;
        this.chosenValue = value;
        this.inputControl.setValue(value);
        this.valueChosen.next(value);
    }

    send() {
        this.valueChosen.next(this._value);
    }
}
